import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { ThemeProvider } from "@/hooks/useTheme";
import { AppLayout } from "@/components/layout/AppLayout";
import { RequireAuth } from "@/hooks/useAuth";
import { ActiveOrgProvider } from "@/contexts/ActiveOrgContext";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/layout/OfflineBanner";
import { BuildIndicator } from "@/components/layout/BuildIndicator";
import { lazy, Suspense } from "react";

// Eager-loaded (critical path)
import Auth from "@/pages/Auth";
import Join from "@/pages/Join";
import Hub from "@/pages/Hub";
import NotFound from "@/pages/NotFound";

// Lazy-loaded pages
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const DebugCache = lazy(() => import("@/pages/DebugCache"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Dossiers = lazy(() => import("@/pages/Dossiers"));
const EtatsFinanciers = lazy(() => import("@/pages/EtatsFinanciers"));
const Verification = lazy(() => import("@/pages/Verification"));
const PointageDGI = lazy(() => import("@/pages/PointageDGI"));
const Exports = lazy(() => import("@/pages/Exports"));
const Archives = lazy(() => import("@/pages/Archives"));
const Consolidation = lazy(() => import("@/pages/Consolidation"));
const SettingsProfile = lazy(() => import("@/pages/SettingsProfile"));
const SettingsOrganization = lazy(() => import("@/pages/SettingsOrganization"));
const DossierWorkspace = lazy(() => import("@/pages/DossierWorkspace"));
const Modules = lazy(() => import("@/pages/Modules"));
const AdminLicences = lazy(() => import("@/pages/AdminLicences"));
const AdminAuditLogs = lazy(() => import("@/pages/AdminAuditLogs"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 60_000, // 1 min default
      retry: 1,
    },
  },
});

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-full min-h-[200px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

/** Wrapper that provides ActiveOrgProvider + Realtime for all authenticated routes */
function AuthenticatedShell() {
  useRealtimeSync();
  return (
    <ActiveOrgProvider>
      <Outlet />
    </ActiveOrgProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <OfflineBanner />
      <BuildIndicator />
      <ErrorBoundary fallbackTitle="L'application a rencontré une erreur">
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/join" element={<Join />} />
          <Route path="/" element={<Navigate to="/hub" replace />} />

          {/* All authenticated routes — wrapped with RequireAuth + ActiveOrgProvider */}
          <Route element={<RequireAuth><AuthenticatedShell /></RequireAuth>}>
            {/* Hub, onboarding, profile — no AppLayout (no org required) */}
            <Route path="/hub" element={<Hub />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/settings/profile" element={<SettingsProfile />} />
            <Route path="/settings" element={<Navigate to="/settings/organization" replace />} />
            <Route path="/debug/cache" element={<DebugCache />} />


            {/* Org-scoped routes — with AppLayout (header) */}
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/modules" element={<Modules />} />
              <Route path="/dossiers" element={<Dossiers />} />
              <Route path="/dossiers/:id" element={<DossierWorkspace />} />
              <Route path="/etats-financiers" element={<EtatsFinanciers />} />
              <Route path="/verification" element={<Verification />} />
              <Route path="/pointage-dgi" element={<PointageDGI />} />
              <Route path="/consolidation" element={<Consolidation />} />
              <Route path="/exports" element={<Exports />} />
              <Route path="/archives" element={<Archives />} />
              <Route path="/settings/organization" element={<SettingsOrganization />} />
              <Route path="/admin/licences" element={<AdminLicences />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
